import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { useLogin } from '../api/auth';
import { ApiError } from '../api/client';
import { Field } from '../components/Field';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginValues = z.infer<typeof loginSchema>;

export function Login() {
  const login = useLogin();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/events';
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({ resolver: zodResolver(loginSchema) });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await login.mutateAsync(data);
      if (next === '/events' && result.events.length > 0) {
        navigate(`/events/${result.events[0].id}/setup`, { replace: true });
      } else {
        navigate(next, { replace: true });
      }
    } catch (err) {
      // Spec §7 verbatim wording. 5xx is described as a toast in the spec but
      // the v0.4.0 slice has no toast infrastructure (only inline form errors
      // and EventSetup's aria-live region), so we surface 5xx inline alongside
      // the other auth errors.
      if (err instanceof ApiError) {
        if (err.code === 'invalid_credentials' || err.status === 401) {
          setError('root', { message: "Email or password didn't match." });
        } else if (err.status === 429) {
          setError('root', { message: 'Too many attempts. Try again in a few minutes.' });
        } else if (err.status >= 500 || err.code === 'unknown') {
          setError('root', { message: "Couldn't reach the server." });
        } else {
          setError('root', { message: 'Sign-in failed. Try again.' });
        }
      } else {
        // Network error / fetch threw — treat like "can't reach the server".
        setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  return (
    <div className="login-screen">
      <div className="login-card">
        <h1>InkyPay Console</h1>
        <div className="login-sub">Sign in to your tenant.</div>
        <form onSubmit={onSubmit} noValidate>
          <Field label="Email" error={errors.email?.message}>
            <input type="email" autoComplete="username" {...register('email')} />
          </Field>
          <Field label="Password" error={errors.password?.message}>
            <input type="password" autoComplete="current-password" {...register('password')} />
          </Field>
          {errors.root && <div className="form-error">{errors.root.message}</div>}
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
